import { ExpandMore, SettingsBackupRestore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DraggableDialog } from '~/components/Dialogs/components/DraggableDialog';
import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';
import {
  NewEquipmentDialogMode,
  UseNewEquipmentDialogProps,
  useNewEquipmentDialog,
} from '~/scenes/PeopleModule/components/Tabs/EquipmentTab/EquipmentTable/NewEquipmentDialog/useNewEquipmentDialog';
import { useSxStyles } from '~/scenes/PeopleModule/components/Tabs/EquipmentTab/EquipmentTable/NewEquipmentDialog/useStyles';

export type NewEquipmentDialogProps = {
  isOpen: boolean;
  onClose: () => void;
} & UseNewEquipmentDialogProps;

export const NewEquipmentDialog = (props: NewEquipmentDialogProps) => {
  const { isOpen, ...otherProps } = props;

  const sxStyles = useSxStyles();

  const {
    dialogMode,
    equipmentTypes,
    companyEquipmentOptions,
    nextEquipmentNumber,
    form: { control },
    onSubmit,
    onClose,
  } = useNewEquipmentDialog(otherProps);

  const { t } = useTranslation();

  return (
    <DraggableDialog open={isOpen} onClose={onClose}>
      <SolaForceDialogTitle onClose={onClose}>
        {t('new equipment')}
      </SolaForceDialogTitle>
      <DialogContent dividers={true}>
        <Accordion
          expanded={
            dialogMode.mode === NewEquipmentDialogMode.ExistingCompanyEquipment
          }
          onChange={dialogMode.onToggleMode(
            NewEquipmentDialogMode.ExistingCompanyEquipment,
          )}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            {t('laAddFromCompanyEquipment')}
          </AccordionSummary>
          <AccordionDetails>
            <Controller
              control={control}
              name={'companyEquipmentId'}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                const fieldValues = value === -1 ? '' : value;

                return (
                  <FormControl fullWidth error={!!error}>
                    <InputLabel
                      id={'existing-equipment-label'}
                      error={!!error}
                      size={'small'}
                    >
                      {t('laCompanyEquipment')}
                    </InputLabel>
                    <Select
                      labelId={'existing-equipment-label'}
                      label={t('laCompanyEquipment')}
                      value={fieldValues}
                      variant={'outlined'}
                      error={!!error}
                      size={'small'}
                      onChange={(event) => {
                        const resolveId = (
                          value: string | number,
                        ): number | undefined => {
                          if (typeof value === 'number') {
                            return value;
                          }

                          const id = parseInt(value);
                          return isNaN(id) ? undefined : id;
                        };

                        onChange(resolveId(event.target.value));
                      }}
                    >
                      {companyEquipmentOptions.map((option, index) => {
                        const { id, type, item, inventoryNumber } = option;

                        const typeName =
                          equipmentTypes.find((et) => et.code === type)?.name ??
                          '';

                        return (
                          <MenuItem key={index} value={id}>
                            <ListItemText
                              primary={`${typeName} | ${inventoryNumber}`}
                              secondary={item}
                            />
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {!!error && (
                      <FormHelperText error={!!error}>
                        {error.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                );
              }}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={
            dialogMode.mode === NewEquipmentDialogMode.AddNewCompanyEquipment
          }
          onChange={dialogMode.onToggleMode(
            NewEquipmentDialogMode.AddNewCompanyEquipment,
          )}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            {t('laNewCompanyEquipment')}
          </AccordionSummary>
          <AccordionDetails sx={sxStyles.newContent}>
            <Box sx={sxStyles.newFields}>
              <Controller
                control={control}
                name={'type'}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => {
                  return (
                    <FormControl fullWidth error={!!error}>
                      <InputLabel
                        id={'equipment-type-label'}
                        error={!!error}
                        size={'small'}
                      >
                        {t('laType')}
                      </InputLabel>
                      <Select
                        labelId={'equipment-type-label'}
                        label={t('laType')}
                        value={value ?? ''}
                        variant={'outlined'}
                        error={!!error}
                        onChange={onChange}
                        size={'small'}
                      >
                        {equipmentTypes.map((option, index) => {
                          const { code, name } = option;

                          return (
                            <MenuItem key={index} value={code}>
                              {name}
                            </MenuItem>
                          );
                        })}
                      </Select>
                      {!!error && (
                        <FormHelperText error={!!error}>
                          {error.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  );
                }}
              />

              <Controller
                control={control}
                name={'item'}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => {
                  return (
                    <TextField
                      value={value}
                      onChange={onChange}
                      label={t('laEquipmentItem')}
                      fullWidth={true}
                      error={!!error}
                      helperText={error?.message}
                      size={'small'}
                    />
                  );
                }}
              />

              <Controller
                control={control}
                name={'inventoryNumber'}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => {
                  return (
                    <Box sx={sxStyles.newInventoryNumber}>
                      <TextField
                        value={value}
                        onChange={onChange}
                        label={t('laEquipmentNumber')}
                        fullWidth={true}
                        error={!!error}
                        helperText={error?.message}
                        size={'small'}
                      />
                      <IconButton
                        onClick={() => onChange(nextEquipmentNumber)}
                        disabled={value === nextEquipmentNumber}
                        title={t('laEquipmentNumber')}
                      >
                        <SettingsBackupRestore />
                      </IconButton>
                    </Box>
                  );
                }}
              />
            </Box>
            <Alert variant="outlined" severity="warning">
              {t('valuesCannotBeChangedLater')}
            </Alert>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSubmit}>{t('continue')}</Button>
      </DialogActions>
    </DraggableDialog>
  );
};
